<template>
  <button
    :type="type"
    :disabled="disabled"
    :aria-label="label"
    :aria-disabled="disabled ? 'true' : undefined"
    :class="classes"
    @click="$emit('click', $event)"
  >
    <slot />
  </button>
</template>

<script setup lang="ts">
import { computed } from 'vue';
import { cva, type VariantProps } from 'class-variance-authority';

const iconButtonVariants = cva(
  [
    'inline-flex items-center justify-center rounded-md transition-colors duration-150',
    'focus-visible:outline-none focus-visible:shadow-[var(--shadow-ring-focus)]',
    'disabled:cursor-not-allowed disabled:opacity-60',
  ],
  {
    variants: {
      variant: {
        primary: 'bg-accent text-ink-inverse hover:bg-primary-400 active:bg-primary-600',
        ghost: 'bg-transparent text-ink hover:bg-paper-muted',
        danger: 'bg-danger-500 text-ink-inverse hover:bg-danger-700',
      },
      size: {
        sm: 'h-9 w-9',
        md: 'h-11 w-11',
        lg: 'h-14 w-14',
      },
    },
    defaultVariants: {
      variant: 'ghost',
      size: 'md',
    },
  },
);

export type IconButtonVariant = NonNullable<VariantProps<typeof iconButtonVariants>['variant']>;
export type IconButtonSize = NonNullable<VariantProps<typeof iconButtonVariants>['size']>;

interface Props {
  label: string;
  variant?: IconButtonVariant;
  size?: IconButtonSize;
  type?: 'button' | 'submit' | 'reset';
  disabled?: boolean;
}

const props = withDefaults(defineProps<Props>(), {
  variant: 'ghost',
  size: 'md',
  type: 'button',
  disabled: false,
});

defineEmits<{
  (e: 'click', event: MouseEvent): void;
}>();

const classes = computed(() => iconButtonVariants({ variant: props.variant, size: props.size }));
</script>
